/**
 * ResetGiftFunnelWidget
 * --------------------
 * Compact lead-funnel summary for the Reset Gift application:
 *   inquiries  →  submitted  +  ineligible (self-screened)
 *
 * The widget self-hides when the admin clicks "Hide widget" or when the
 * "Clear all & hide" action is used after the campaign ends.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, EyeOff, Gift, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, accent = '#1A3C34', testId }) => (
  <div className="flex-1 min-w-0">
    <div
      className="text-xs uppercase tracking-[2px] mb-1"
      style={{ color: 'rgba(26,60,52,0.55)', fontFamily: "'Manrope', sans-serif" }}
    >
      {label}
    </div>
    <div
      className="text-3xl font-light"
      style={{ color: accent, fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      data-testid={testId}
    >
      {value ?? '—'}
    </div>
  </div>
);

const ResetGiftFunnelWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(true);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearConfirm, setClearConfirm] = useState('');
  const [clearAlsoHides, setClearAlsoHides] = useState(true);
  const [clearing, setClearing] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/gift-application/funnel-stats`,
        { headers: authHeaders() }
      );
      setStats(res.data);
      setVisible(res.data?.widget_visible !== false);
    } catch (err) {
      console.warn('[ResetGiftFunnel] stats fetch failed', err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleHide = async () => {
    if (!window.confirm('Hide the Reset Gift funnel widget? Data is preserved; admins can show it again later via API.')) return;
    try {
      await axios.put(
        `${API_URL}/api/admin/gift-application/widget-visibility`,
        { visible: false },
        { headers: authHeaders() }
      );
      setVisible(false);
      toast.success('Widget hidden');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to hide widget');
    }
  };

  const handleClear = async () => {
    if (clearConfirm !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setClearing(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/admin/gift-application/clear-all`,
        {
          headers: authHeaders(),
          data: { confirm: 'DELETE', hide_widget: clearAlsoHides },
        }
      );
      toast.success(
        `Cleared ${res.data?.applications_deleted || 0} applications and ${res.data?.inquiries_deleted || 0} inquiries.`
      );
      setShowClearDialog(false);
      setClearConfirm('');
      if (res.data?.widget_hidden) setVisible(false);
      else fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear');
    } finally {
      setClearing(false);
    }
  };

  if (!visible && !loading) return null;

  return (
    <>
      <Card
        className="card-base border-0"
        style={{
          backgroundColor: '#FFF7F2',
          borderLeft: '4px solid #C06C54',
        }}
        data-testid="reset-gift-funnel-widget"
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle
              className="text-lg flex items-center gap-2"
              style={{ color: '#1A3C34', fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              <Gift className="h-5 w-5" style={{ color: '#C06C54' }} />
              Reset Gift Lead Funnel
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchStats}
                disabled={loading}
                data-testid="reset-gift-funnel-refresh"
                title="Refresh"
              >
                <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleHide}
                data-testid="reset-gift-funnel-hide"
                title="Hide widget (data preserved)"
              >
                <EyeOff className="h-3 w-3 mr-1" /> Hide
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setShowClearDialog(true)}
                data-testid="reset-gift-funnel-clear"
                title="Permanently delete all applications & hide widget"
              >
                <Trash2 className="h-3 w-3 mr-1" /> Clear &amp; close
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-6 flex-wrap">
            <Stat
              label="Inquiries (Step 1)"
              value={stats?.inquiries}
              accent="#1A3C34"
              testId="funnel-stat-inquiries"
            />
            <div
              className="text-2xl font-light hidden sm:block"
              style={{ color: 'rgba(26,60,52,0.3)', fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              aria-hidden
            >
              →
            </div>
            <Stat
              label="Submitted"
              value={stats?.submitted}
              accent="#1A3C34"
              testId="funnel-stat-submitted"
            />
            <Stat
              label="Self-screened out"
              value={stats?.ineligible}
              accent="#C06C54"
              testId="funnel-stat-ineligible"
            />
          </div>
          <p
            className="text-xs mt-4 leading-relaxed"
            style={{ color: 'rgba(26,60,52,0.5)', fontFamily: "'Manrope', sans-serif" }}
          >
            Inquiries fire when an applicant completes Step 1 (name/email/phone). Submitted = full applications. Self-screened = applicants who answered "No" to airfare / commitment / 48-hour secure on Step 3. All three segments are tagged in Ontraport.
          </p>
        </CardContent>
      </Card>

      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clear Reset Gift data?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              This will permanently delete <strong>{stats?.submitted || 0} submitted applications</strong>, <strong>{stats?.inquiries || 0} inquiries</strong>, and <strong>{stats?.ineligible || 0} self-screened records</strong> from the platform. This cannot be undone.
            </p>
            <p className="text-sm text-gray-600">
              Type <strong>DELETE</strong> to confirm:
            </p>
            <Input
              value={clearConfirm}
              onChange={(e) => setClearConfirm(e.target.value)}
              placeholder="DELETE"
              data-testid="clear-confirm-input"
            />
            <Label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={clearAlsoHides}
                onChange={(e) => setClearAlsoHides(e.target.checked)}
                className="rounded accent-[#C06C54]"
                data-testid="clear-also-hides"
              />
              Also hide this widget after clearing (recommended once campaign ends)
            </Label>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowClearDialog(false)}
              disabled={clearing}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleClear}
              disabled={clearing || clearConfirm !== 'DELETE'}
              data-testid="clear-confirm-btn"
            >
              {clearing ? 'Clearing…' : 'Permanently delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResetGiftFunnelWidget;
